import { useMemo, useState } from "react";
import { Check, X, Sparkles, Send, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { submitContact } from "@/lib/api";

const DEFAULTS = {
  ar: {
    tag: "الباقات والأسعار",
    title: "خطط تناسب كل احتياج",
    subtitle: "اختر الباقة الأنسب لحجم عملك — كل الخطط قابلة للتخصيص بالكامل.",
    popular_badge: "الأكثر طلبًا",
    per_project: "لكل مشروع",
    cta_book: "احجز عرض سعر مخصص",
    cta_period: "USD",
    plans: [
      {
        name: "Basic",
        price: "1,500",
        period: "من / يبدأ",
        desc: "مناسب للشركات الناشئة والمشاريع الصغيرة.",
        features: [
          "+ موقع تعريفي متجاوب",
          "+ صفحة هبوط واحدة",
          "+ دعم فني 30 يوم",
          "+ استضافة سنة واحدة",
          "- تكامل مع أنظمة خارجية",
          "- تطبيق موبايل",
        ],
      },
      {
        name: "Professional",
        price: "5,000",
        period: "من / يبدأ",
        desc: "للشركات المتوسطة الراغبة بحلول متكاملة.",
        popular: true,
        features: [
          "+ كل ميزات Basic",
          "+ نظام إدارة داخلي (ERP/CRM)",
          "+ لوحة تحكم متقدمة",
          "+ تكامل مع بوابات الدفع",
          "+ دعم فني 6 أشهر",
          "- تطبيق موبايل مخصص",
        ],
      },
      {
        name: "Enterprise",
        price: "حسب الطلب",
        period: "",
        desc: "حلول مؤسسية كاملة بمقاييس عالمية.",
        features: [
          "+ كل ميزات Professional",
          "+ تطبيق موبايل iOS / Android",
          "+ ذكاء اصطناعي مخصص",
          "+ تكامل مع أي نظام خارجي",
          "+ SLA 99.9% ودعم 24/7",
          "+ مدير مشروع مخصص",
        ],
      },
    ],
  },
  en: {
    tag: "Pricing & Plans",
    title: "Plans that fit every need",
    subtitle: "Choose the plan that matches your business size — every plan is fully customizable.",
    popular_badge: "Most Popular",
    per_project: "per project",
    cta_book: "Request Custom Quote",
    cta_period: "USD",
    plans: [
      { name: "Basic", price: "1,500", period: "starting from", desc: "For startups and small projects.",
        features: ["+ Responsive website", "+ Single landing page", "+ 30-day support", "+ 1-year hosting", "- External integrations", "- Mobile app"] },
      { name: "Professional", price: "5,000", period: "starting from", desc: "For mid-size companies wanting complete solutions.", popular: true,
        features: ["+ All Basic features", "+ Internal ERP/CRM", "+ Advanced dashboard", "+ Payment gateways", "+ 6-month support", "- Custom mobile app"] },
      { name: "Enterprise", price: "On Request", period: "", desc: "Full enterprise solutions at global standards.",
        features: ["+ All Professional features", "+ iOS / Android app", "+ Custom AI features", "+ Any external integration", "+ 99.9% SLA + 24/7", "+ Dedicated PM"] },
    ],
  },
  ku: {
    tag: "پاکێج و نرخەکان",
    title: "پلانی گونجاو بۆ هەموو پێداویستییەک",
    subtitle: "پلانی گونجاو بۆ قەبارەی کارەکەت هەڵبژێرە — هەموو پلانەکان دەگۆڕدرێن.",
    popular_badge: "زۆرترین داواکراو",
    per_project: "بۆ هەر پرۆژە",
    cta_book: "داواکاری نرخی تایبەت",
    cta_period: "USD",
    plans: [
      { name: "Basic", price: "1,500", period: "دەستپێدەکات لە", desc: "بۆ کۆمپانیا نوێکان و پرۆژە بچووکەکان.",
        features: ["+ ماڵپەڕی گشتی", "+ یەک پەڕەی لاندینگ", "+ پشتگیری ٣٠ ڕۆژ", "+ هێستنگ ١ ساڵ", "- بەیەکەوەبەستنی سیستەم", "- ئەپی مۆبایل"] },
      { name: "Professional", price: "5,000", period: "دەستپێدەکات لە", desc: "بۆ کۆمپانیا مامناوەندەکان.", popular: true,
        features: ["+ هەموو تایبەتمەندییەکانی Basic", "+ ERP/CRM ی ناوخۆیی", "+ داشبۆردی پێشکەوتوو", "+ گەیت وەیی پارە", "+ پشتگیری ٦ مانگ", "- ئەپی مۆبایلی تایبەت"] },
      { name: "Enterprise", price: "بەپێی داواکاری", period: "", desc: "چارەسەری تەواوی مۆسسات.",
        features: ["+ هەموو تایبەتمەندییەکانی Professional", "+ ئەپی iOS / Android", "+ AI تایبەت", "+ بەیەکەوەبەستن", "+ SLA 99.9% + 24/7", "+ بەڕێوەبەری تایبەت"] },
    ],
  },
};

function QuoteModal({ plan, onClose, lang }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const inputCls = "w-full rounded-xl bg-[#0b1731] border border-white/10 focus:border-[color:var(--primary)] px-4 py-2.5 text-sm outline-none";

  const L = {
    ar: { title: "احجز عرض سعر", name: "الاسم", email: "البريد الإلكتروني", phone: "الهاتف", msg: "متطلباتك", submit: "إرسال الطلب", sending: "جارٍ الإرسال...", ok: "تم استلام طلبك — سنتواصل معك خلال 24 ساعة", close: "إغلاق" },
    en: { title: "Request a Quote", name: "Name", email: "Email", phone: "Phone", msg: "Your requirements", submit: "Send Request", sending: "Sending...", ok: "We've received your request — we'll contact you within 24h", close: "Close" },
    ku: { title: "داواکاری نرخ", name: "ناو", email: "ئیمەیل", phone: "مۆبایل", msg: "پێداویستیەکانت", submit: "ناردن", sending: "لە ناردندایە...", ok: "داواکارییەکەت وەرگیرا", close: "داخستن" },
  }[lang] || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || busy) return;
    setBusy(true);
    try {
      await submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: `[Plan: ${plan.name}] ${form.message}`.trim(),
        type: "quote",
        lang,
      });
      setDone(true);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="pricing-quote-modal"
    >
      <motion.div
        className="relative w-full max-w-md rounded-3xl card-surface p-6 sm:p-8 my-8"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 end-4 h-8 w-8 grid place-items-center rounded-full border border-white/10 hover:border-[color:var(--primary)] text-slate-400" data-testid="pricing-quote-close">
          <X size={16} />
        </button>
        {done ? (
          <div className="text-center py-6">
            <Check size={64} className="text-primary mx-auto mb-4" />
            <p className="text-slate-300 mb-6">{L.ok}</p>
            <button onClick={onClose} className="rounded-full px-6 py-2.5 font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>{L.close}</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <h3 className="text-2xl font-extrabold mb-1">{L.title}</h3>
            <div className="text-sm text-primary font-semibold mb-3">{plan.name}</div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={L.name + " *"} className={inputCls} data-testid="pricing-quote-name" />
            <input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={L.email} className={inputCls} data-testid="pricing-quote-email" />
            <input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={L.phone + " *"} className={inputCls} data-testid="pricing-quote-phone" />
            <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={L.msg} className={inputCls} data-testid="pricing-quote-msg" />
            <button type="submit" disabled={busy} className="w-full rounded-full py-3 font-extrabold text-[#04120a] disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: "var(--primary)" }} data-testid="pricing-quote-submit">
              {busy ? <><Loader2 size={16} className="animate-spin" /> {L.sending}</> : <><Send size={16} /> {L.submit}</>}
            </button>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}

// Parse "+ Feature" or "- Feature" into { text, included }
const parseFeature = (line) => {
  const s = String(line || "").trim();
  if (!s) return null;
  if (s.startsWith("+")) return { text: s.slice(1).trim(), included: true };
  if (s.startsWith("-")) return { text: s.slice(1).trim(), included: false };
  return { text: s, included: true };
};

export default function Pricing() {
  const { lang } = useLang();
  const { settings } = useSite();
  const d = DEFAULTS[lang] || DEFAULTS.ar;
  const [selectedPlan, setSelectedPlan] = useState(null);

  const tag = settings[`pricing_tag_${lang}`] || d.tag;
  const title = settings[`pricing_title_${lang}`] || d.title;
  const subtitle = settings[`pricing_subtitle_${lang}`] || d.subtitle;

  const plans = useMemo(() => {
    return [1, 2, 3].map((i) => {
      const df = d.plans[i - 1];
      const nameOverride = settings[`pricing_plan${i}_name_${lang}`] || df.name;
      const priceOverride = settings[`pricing_plan${i}_price`] || df.price;
      const periodOverride = settings[`pricing_plan${i}_period_${lang}`] || df.period;
      const descOverride = settings[`pricing_plan${i}_desc_${lang}`] || df.desc;
      const featuresOverride = settings[`pricing_plan${i}_features_${lang}`];
      const featureLines = (featuresOverride ? featuresOverride.split(/\n/) : df.features).map(parseFeature).filter(Boolean);
      const popular = settings[`pricing_plan${i}_popular`] !== undefined
        ? String(settings[`pricing_plan${i}_popular`]).toLowerCase() === "true"
        : !!df.popular;
      return { name: nameOverride, price: priceOverride, period: periodOverride, desc: descOverride, features: featureLines, popular };
    });
  }, [settings, lang, d]);

  return (
    <section id="pricing" className="relative section-pad overflow-hidden" data-testid="pricing-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,242,96,0.08), transparent 60%)" }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-12">
          <Reveal><SectionTag>{tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{subtitle}</p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-5 sm:gap-6 items-stretch">
          {plans.map((p, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <div
                className={"relative rounded-3xl p-6 sm:p-8 h-full flex flex-col transition-all duration-500 hover:-translate-y-2 " + (p.popular ? "border-2" : "card-surface")}
                style={p.popular ? {
                  background: "linear-gradient(180deg, rgba(0,242,96,0.08), rgba(10,17,40,0.9))",
                  borderColor: "var(--primary)",
                  boxShadow: "0 20px 60px -20px rgba(0,242,96,0.4)",
                } : {}}
                data-testid={`pricing-plan-${i}`}
              >
                {p.popular && (
                  <div className="absolute -top-3 start-1/2 -translate-x-1/2 rounded-full px-4 py-1 text-[11px] font-extrabold text-[#04120a] inline-flex items-center gap-1.5" style={{ background: "var(--primary)" }}>
                    <Sparkles size={12} /> {d.popular_badge}
                  </div>
                )}
                <div className="mb-6">
                  <h3 className="text-2xl font-extrabold mb-2">{p.name}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed min-h-[3rem]">{p.desc}</p>
                </div>
                <div className="mb-6 pb-6 border-b border-white/10">
                  {p.period && <div className="text-xs text-slate-500 mb-1">{p.period}</div>}
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl sm:text-5xl font-black text-primary tabular-nums" style={{ textShadow: "0 0 20px rgba(0,242,96,0.3)" }}>
                      {/^\d/.test(p.price) ? `$${p.price}` : p.price}
                    </span>
                    {/^\d/.test(p.price) && <span className="text-xs text-slate-500">{d.cta_period}</span>}
                  </div>
                </div>
                <ul className="space-y-3 mb-8 flex-1" data-testid={`pricing-features-${i}`}>
                  {p.features.map((f, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm">
                      {f.included ? (
                        <Check size={17} className="text-primary shrink-0 mt-0.5" />
                      ) : (
                        <X size={17} className="text-slate-600 shrink-0 mt-0.5" />
                      )}
                      <span className={f.included ? "text-slate-300" : "text-slate-500 line-through decoration-slate-700"}>{f.text}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setSelectedPlan(p)}
                  className={"w-full rounded-full py-3 font-extrabold text-sm transition-all " + (p.popular ? "text-[#04120a]" : "border border-[color:var(--primary)]/60 text-primary hover:bg-[color:var(--primary)] hover:text-[#04120a]")}
                  style={p.popular ? { background: "var(--primary)", boxShadow: "0 8px 30px rgba(0,242,96,0.35)" } : {}}
                  data-testid={`pricing-cta-${i}`}
                >
                  {d.cta_book}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <AnimatePresence>
        {selectedPlan && <QuoteModal plan={selectedPlan} lang={lang} onClose={() => setSelectedPlan(null)} />}
      </AnimatePresence>
    </section>
  );
}
